/**
 * The external dependencies
 */
/** @jsxRuntime classic */
/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Box,
  Typography,
  Paper,
} from '@mui/material';
import {
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
  ProcessListForChangeDto,
  ProcessProjectForChangeDto,
  UpdateChangeDtoStatusEnum,
  UpdateProcessDtoEvaluationStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
/**
 * The internal dependencies
 */
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info'; // Or any other icon

import ProcessAccordionForm from './ProcessAccordionForm';

import LoadSpinnerSmall from '../LoadSpinner/LoadSpinnerSmall';
import { useChangeStore } from '../../store/changeStore';
import { isChangeManager, isKTM } from '../../utils/permissionHelper';
import WorkTabPanel from '../WorkTabPanels/WorkTabPanel';
import ProcessAccordionTitle from '../ProcessItems/ProcessAccordionTitle';

export type ProcessAccordionProps = {
  expanded: boolean;
  workPackages: ListWorkPackageDto[];
  workServices: ListWorkServiceDto[];
  workMaterials: ListWorkMaterialDto[];
  changeId?: string;
  process: ProcessListForChangeDto;
  isImplementedBtnVisible: boolean;
  isLoading: boolean;
  handleChange: (fieldName: string, fieldValue: string | boolean) => void;
  handleExpand: () => void;
  handleWorkPackageModal: (workPackageId?: number) => void;
  handleServiceModal: (workServiceId?: number) => void;
  handleMaterialModal: (workMaterialId?: number) => void;
  handleUpdateAllWorkPackages: () => void;
  SummaryLabels?: React.ReactNode;
  status: string | null | undefined;
  department: ProcessProjectForChangeDto | undefined;
};

const ProcessAccordion = observer(
  ({
    process,
    expanded,
    workPackages,
    workServices,
    workMaterials,
    isImplementedBtnVisible,
    isLoading,
    SummaryLabels,
    status,
    handleChange,
    handleWorkPackageModal,
    handleServiceModal,
    handleMaterialModal,
    handleExpand,
    handleUpdateAllWorkPackages,
    department,
    ...props
  }: ProcessAccordionProps) => {
    const isDisabled = workPackages.every(wP => wP.status === 'IMPLEMENTED');
    const { t } = useTranslation();
    const { change } = useChangeStore();
    const [tabIndex, setTabIndex] = useState(0);

    const disableFinishProcess =
      (!process.affected && workPackages.length === 0) ||
      (change?.status !== UpdateChangeDtoStatusEnum.InEvaluation &&
        change?.status !== UpdateChangeDtoStatusEnum.EvalRework);

    const isFlightIncluded =
      workPackages && workPackages.some(wP => wP.travelIncluded);

    const permissionToEdit =
      isKTM(change, process.pId) || isChangeManager(change);

    const needsRework =
      status === UpdateProcessDtoEvaluationStatusEnum.OpenAfterRework;

    return (
      <Accordion
        expanded={expanded}
        onChange={handleExpand}
        css={css`
          background-color: white;
          margin-bottom: 16px;
          &::before {
            display: none;
          }

          & .MuiAccordionSummary-gutters {
            padding-left: 10px;
          }
          &.Mui-expanded {
            margin: 0;
            margin-bottom: 16px;
          }
        `}
        elevation={3}
        {...props}
      >
        <AccordionSummary
          expandIcon={
            <ExpandMoreIcon
              color="primary"
              css={css`
                transform: rotate(-90deg);
              `}
            />
          }
          css={css`
            flex-direction: row-reverse;
            gap: 16px;
            height: 70px;
            align-items: center;
            & .MuiAccordionSummary-content ~ .Mui-expanded {
              transform: rotate(90deg) !important;
            }
          `}
          sx={{ background: expanded ? '#f8fafc' : 'white' }}
        >
          {needsRework && (
            <WarningIcon
              color="error"
              sx={{ display: 'flex', alignSelf: 'center' }}
            />
          )}

          {department && <ProcessAccordionTitle department={department} />}

          {SummaryLabels}
          {expanded && isLoading && <LoadSpinnerSmall />}
        </AccordionSummary>

        <AccordionDetails
          css={css`
            display: flex;
            padding: 0px;
            margin-top: -12px;
            flex-direction: column;
            @media (max-width: 690px) {
              flex-direction: column;
            }

            @media (max-width: 600px) {
              padding: 56px 0px 0px;
            }
          `}
        >
          <Box
            sx={{
              display: 'flex',
              width: '100%',
              background: '#f8fafc',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid #e3e9f1',
              padding: 1,
            }}
          >
            {expanded && (
              <ProcessAccordionForm
                handleChange={handleChange}
                process={process}
                workPackages={workPackages}
                workMaterials={workMaterials}
                workServices={workServices}
              />
            )}
            {workPackages.length === 0 &&
              (workServices.length !== 0 || workMaterials.length !== 0) && (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: 400,
                    gap: 2,
                  }}
                >
                  <InfoIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                  <Typography variant="body1" color="text.secondary">
                    {t('workPackage.minWorkPackageText')}
                  </Typography>
                </Paper>
              )}
            {!isImplementedBtnVisible &&
              !process.affected &&
              (change?.status === UpdateChangeDtoStatusEnum.InEvaluation ||
                change?.status === UpdateChangeDtoStatusEnum.EvalRework) && (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    width: 'fit-content',
                    alignItems: 'center',
                    marginRight: 4,
                  }}
                >
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      handleWorkPackageModal();
                      setTabIndex(0);
                    }}
                    startIcon={<AddIcon />}
                  >
                    {t('workPackage.workPackage')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      handleServiceModal();
                      setTabIndex(1);
                    }}
                    startIcon={<AddIcon />}
                  >
                    {t('service.service')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => {
                      handleMaterialModal();
                      setTabIndex(2);
                    }}
                    startIcon={<AddIcon />}
                  >
                    {t('material.material')}
                  </Button>
                </Box>
              )}

            {!isImplementedBtnVisible && (
              <Button
                onClick={() =>
                  handleChange(
                    'evaluationStatus',
                    process.evaluationStatus === 'APPROVED'
                      ? 'OPEN'
                      : 'APPROVED',
                  )
                }
                variant="text"
                disabled={!permissionToEdit || disableFinishProcess}
              >
                {process.evaluationStatus === 'APPROVED'
                  ? t('buttons.reopenProcess')
                  : t('buttons.finishprocess')}
              </Button>
            )}
            {isImplementedBtnVisible && !process.affected && (
              <Button
                disabled={isDisabled || !permissionToEdit}
                onClick={handleUpdateAllWorkPackages}
                variant="text"
                css={css`
                  top: 16px;

                  @media (max-width: 690px) {
                    position: absolute;
                    top: 70px;
                    right: 24px;
                  }
                `}
              >
                {t('buttons.finishImpl')}
              </Button>
            )}
          </Box>

          <WorkTabPanel
            workPackages={workPackages}
            isFlightIncluded={isFlightIncluded}
            workServices={workServices}
            workMaterials={workMaterials}
            department={department}
            process={process}
            tabIndex={tabIndex}
            handleWorkPackageModal={(id: number) => handleWorkPackageModal(id)}
            handleServiceModal={(id?: number) => handleServiceModal(id)}
            handleMaterialModal={(id?: number) => handleMaterialModal(id)}
          />
        </AccordionDetails>
      </Accordion>
    );
  },
);

export default ProcessAccordion;
